// Library Methods
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import html2canvas from "html2canvas-pro";

// Utils
import {
  calculateNeighbours,
  getChartSpots,
  getPatternDevChartSpots,
  getTotalChartSpots,
} from "src/utils/exams/examReportsHelper";
import {
  getExamStats,
  isBeforeBaselineDay,
} from "src/utils/exams/progressionAnalysisHelper";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import ReportError from "../ReportError/ReportError";
import PatternDevGraph from "../ReportSections/ReportGraph/VFThresholdingGraph/VFThresholdingChart/PatternDevGraph/PatternDevGraph";
import PatternGraph from "../ReportSections/ReportGraph/VFThresholdingGraph/VFThresholdingChart/PatternGraph/PatternGraph";
import TotalDevGraph from "../ReportSections/ReportGraph/VFThresholdingGraph/VFThresholdingChart/TotalDevGraph/TotalDevGraph";
import PatternLegend from "../ReportSections/ReportGraph/VFThresholdingGraph/VFThresholdingChart/PatternLegend/PatternLegend";
import ProgressionReportColumns from "../ProgressionReportColumns/ProgressionReportColumns";
import GPAChart from "../ProgressionReportSection/ProgressionGPACharts/GPAChart/GPAChart";
import ProgressionBaselineButton, {
  BaselineActionType,
} from "../ProgressionBaselineButton/ProgressionBaselineButton";

// Types
import {
  EnrichedRetinaLogikSectionType,
  ExamSectionReportType,
  GPAExclusionReason,
  GPAVisitType,
  SelectedProgressionExamType,
} from "src/services/exams/types";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

type ProgressionPatternChartsType = {
  examReport: ExamSectionReportType;
  examData: EnrichedRetinaLogikSectionType | SelectedProgressionExamType;
  gpaVisit?: GPAVisitType;
  gpaExclusion?: GPAExclusionReason;
  isGpaLoading?: boolean;
  // completion date of the analysis's oldest baseline exam
  earliestBaselineDate?: string | null;
  // Which baseline button the row's header shows, if any
  baselineAction?: BaselineActionType;
  onBaselineClick?: () => void;
};

const ProgressionPatternCharts = ({
  examReport,
  examData,
  gpaVisit,
  gpaExclusion,
  isGpaLoading = false,
  earliestBaselineDate,
  baselineAction,
  onBaselineClick,
}: ProgressionPatternChartsType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();

  // Keep the column (and the other three charts' smaller size) whether this row
  // charts a GPA visit or just explains why it has none, so rows stay aligned.
  // Before the earliest baseline day, GPA leaves the exam out entirely
  const isBeforeBaseline = useMemo(
    () =>
      isBeforeBaselineDay(
        examData?.completionDate,
        earliestBaselineDate,
        clinicInfo?.timezone ?? null
      ),
    [examData?.completionDate, earliestBaselineDate, clinicInfo?.timezone]
  );

  const showsGpaColumn =
    Boolean(gpaVisit) || Boolean(gpaExclusion) || isBeforeBaseline;

  const [imageSrc, setImageSrc] = useState<string | null>(null);

  const eyeType = useMemo(() => examReport?.exam?.eye, [examReport?.exam?.eye]);

  const gridType = useMemo(
    () => examReport?.exam?.grid,
    [examReport?.exam?.grid]
  );

  const numberGraphSpots = useMemo(
    () => getChartSpots(examReport) ?? [],
    [examReport]
  );

  const patternGraphSpots = useMemo(
    () => calculateNeighbours(numberGraphSpots) ?? [],
    [numberGraphSpots]
  );

  const patternDevSpots = useMemo(
    () => getPatternDevChartSpots(examReport) ?? [],
    [examReport]
  );

  const totalDevSpots = useMemo(
    () => getTotalChartSpots(examReport) ?? [],
    [examReport]
  );

  const stats = useMemo(() => getExamStats(examReport), [examReport]);

  const handleShowImage = useCallback(async () => {
    const element = document.getElementById(
      `eye-report-charts-${examData?.id}`
    );

    // do nothing if element not loaded
    if (!element) {
      return;
    } else {
      const canvas = await html2canvas(element, {
        scale: 2,
      });
      const data = canvas?.toDataURL("image/png");
      setImageSrc(data);
    }
  }, [examData?.id]);

  useEffect(() => {
    // Wait until GPA has settled (with a visit, an exclusion reason, or
    // neither) before capturing, so this only ever needs to run once instead
    // of capturing early and re-capturing when GPA arrives late.
    if (isGpaLoading) return;

    const timeoutId = setTimeout(() => {
      handleShowImage();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [handleShowImage, isGpaLoading]);

  // The exam badge, with the baseline button under it. A row with no baseline action still
  // reserves the (widest) button's space, so its columns line up with the baseline rows'
  const headerAction = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: "4px",
      }}
    >
      <Typography
        data-testid={`retinalogik-exam-stamp-${examData?.id}`}
        fontSize="12px"
        fontWeight={600}
        color="#E2772E"
        border="1px solid #E2772E"
        borderRadius="4px"
        padding="2px 8px"
        whiteSpace="nowrap"
      >
        {t("RetinaLogik Exam")}
      </Typography>

      {onBaselineClick && (
        <ProgressionBaselineButton
          action={baselineAction ?? "update"}
          onClick={onBaselineClick}
          isHidden={!baselineAction}
        />
      )}
    </Box>
  );

  return (
    <Grid
      container
      className="retinalogik-report"
      data-testid={`eye-report-${examData?.id}`}
      id={`eye-report-${examData?.id}`}
    >
      {/* The header stays live so its baseline button keeps working, and the stats stay live
          text; only the charts between them are swapped for a snapshot. */}
      <Grid
        container
        display="flex"
        border="1px solid #000"
        borderRadius="4px 4px 0px 0px"
        padding="8px"
      >
        <ProgressionReportColumns
          examReport={examReport}
          action={headerAction}
        />
      </Grid>

      {imageSrc ? (
        <img
          src={imageSrc}
          alt="Generated"
          width="100%"
          height="auto"
          style={{ display: "block" }}
        />
      ) : (
        <Grid container id={`eye-report-charts-${examData?.id}`}>
          {examReport?.postProcessing ? (
            <Grid
              container
              sx={{
                display: "flex",
                flexDirection: "column",
                borderLeft: "1px solid #000",
                borderRight: "1px solid #000",
                padding: "8px",
                rowGap: "20px",
              }}
            >
              <Grid
                container
                justifyContent="space-around"
                flexWrap="nowrap"
                columnGap="16px"
                rowGap="20px"
                sx={{ overflowX: "auto" }}
              >
                <Grid
                  item
                  sx={{ display: "flex", flexDirection: "column", gap: "8px" }}
                >
                  <Typography fontSize="12px" textAlign="center">
                    {t("Greyscale")}
                  </Typography>
                  <PatternGraph
                    pressedPoints={patternGraphSpots}
                    eyeType={eyeType}
                    gridType={gridType}
                    containerSize={showsGpaColumn ? 200 : 260}
                  />
                </Grid>

                <Grid
                  item
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Typography fontSize="12px" textAlign="center">
                    {t("Total Deviation Percentile")}
                  </Typography>
                  <TotalDevGraph
                    pressedPoints={totalDevSpots}
                    eyeType={eyeType}
                    gridType={gridType}
                    containerSize={showsGpaColumn ? 200 : 260}
                  />
                </Grid>

                <Grid
                  item
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Typography fontSize="12px" textAlign="center">
                    {t("Pattern Deviation Percentile")}
                  </Typography>
                  <PatternDevGraph
                    pressedPoints={patternDevSpots}
                    eyeType={eyeType}
                    gridType={gridType}
                    containerSize={showsGpaColumn ? 200 : 260}
                  />
                </Grid>

                {showsGpaColumn && (
                  <Grid
                    item
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Typography fontSize="12px" textAlign="center">
                      {t("GPA")}
                    </Typography>
                    {gpaVisit ? (
                      <GPAChart
                        gpaPoints={gpaVisit.gpa_chart.points}
                        containerSize={200}
                      />
                    ) : (
                      <Typography
                        className="retinalogik-analysis-report"
                        fontSize="12px"
                        textAlign="center"
                        sx={{ width: "200px" }}
                      >
                        {isBeforeBaseline
                          ? t(
                              "Not charted — exams before the selected GPA baselines are excluded from progression analysis."
                            )
                          : gpaExclusion === "baseline"
                            ? t(
                                "Baseline exam — GPA compares later exams against it."
                              )
                            : t(
                                "Not charted — another exam from the same day was used for GPA."
                              )}
                      </Typography>
                    )}
                  </Grid>
                )}
              </Grid>

              <Grid
                container
                justifyContent="space-around"
                flexWrap="wrap"
                columnGap="50px"
                rowGap="20px"
              >
                <Grid
                  item
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "8px",
                    minWidth: "235px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PatternLegend displayColumn={false} />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <Grid
              container
              borderLeft="1px solid #000"
              borderRight="1px solid #000"
            >
              <ReportError
                title={t("Post-Processing Data is Not Available.")}
              />
            </Grid>
          )}
        </Grid>
      )}

      {/* Stays live text rather than part of the snapshot */}
      {examReport?.postProcessing && examReport?.stats && (
        <Grid
          container
          display="flex"
          border="1px solid black"
          borderRadius="0px 0px 4px 4px"
          padding="8px"
        >
          <Grid item container xs={12}>
            <Grid item xs>
              <Typography
                data-testid={`fixation-loss-${examData.id}`}
                fontSize="12px"
              >
                <span style={{ fontWeight: 600 }}>{t("FL")}: </span>
                {stats.FL}
              </Typography>
            </Grid>
            <Grid item xs>
              <Typography
                data-testid={`false-negative-${examData.id}`}
                fontSize="12px"
              >
                <span style={{ fontWeight: 600 }}>{t("FN")}: </span>
                {stats.FN}
              </Typography>
            </Grid>
            <Grid item xs>
              <Typography
                data-testid={`false-positive-${examData.id}`}
                fontSize="12px"
              >
                <span style={{ fontWeight: 600 }}>{t("FP")}: </span>
                {stats.FP}
              </Typography>
            </Grid>
          </Grid>
          <Grid item container xs={12}>
            <Grid item xs>
              <Typography
                data-testid={`pattern-standard-deviation-${examData.id}`}
                fontSize="12px"
              >
                <span style={{ fontWeight: 600 }}>{t("PSD")}: </span>
                {stats.PSD}
              </Typography>
            </Grid>
            <Grid item xs>
              <Typography
                data-testid={`mean-deviation-${examData.id}`}
                fontSize="12px"
              >
                <span style={{ fontWeight: 600 }}>{t("MD")}: </span>
                {stats.MD}
              </Typography>
            </Grid>
            <Grid item xs>
              <Typography
                data-testid={`retinalogik-field-index-${examData.id}`}
                fontSize="12px"
              >
                <span style={{ fontWeight: 600 }}>{t("VRFI")}: </span>
                {stats.VRFI}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default ProgressionPatternCharts;
